import { CAMPAIGN_STATUS, CampaignVisualStatus } from "@/@types/campaign";

import { COMMON_TEXTS } from "../utils/messages";

export const CONFLICT_PREVALIDATION_CHUNK_SIZE = 500;
export const LARGE_CAMPAIGN_ITEM_THRESHOLD = 1000;
export const PRO014_MIN_ITEMS = 1;
export const PRO014_MIN_ACTIVE_ITEMS = 2;
export const PRO014_DESCRIPTION_MAX_LENGTH = 40;

export const PRO014_WIZARD_MODE = {
  CREATE: "create",
  EDIT: "edit",
} as const;

export const CONFLICT_FILTER = {
  ALL: "all",
  CONFLICT: "conflict",
  CLEAN: "clean",
} as const;

export const PRO014_RESTRICTION_CHANGE = {
  ENABLE: "ENABLE",
  DISABLE: "DISABLE",
  KEEP: "KEEP",
} as const;

export const PRO014_REVIEW_STATUS = {
  READY: "ready",
  CONFLICTS: "conflicts",
} as const;

export const PRO014_SUBMIT_STATE = {
  VALIDATING: "validating",
  SAVING: "saving",
  IDLE: "idle",
} as const;

export const PRO014_READ_ONLY_STATUS = {
  EXPIRED: CAMPAIGN_STATUS.EXPIRED,
  CANCELLED: CampaignVisualStatus.CANCELLED,
} as const;

export const PRO014_STEP = {
  INFO: 1,
  STORES: 2,
  DISCOUNT: 3,
  PRODUCTS: 4,
  REVIEW: 5,
} as const;

export const PRO014_TOTAL_STEPS = 5;
export const REDIRECT_DELAY_MS = 1500;
export const CAMPAIGNS_LIST_PATH = "/planejamento-comercial/gestao-campanhas";

export const PRO014_DEFAULT_FIELDS = {
  TAKE_QUANTITY: 2,
  PAY_QUANTITY: 1,
  DISCOUNT_VALUE: 0,
  DISCOUNT_TYPE: 4,
  PAYMENT_METHOD: 0,
  BASE_VALUE: 0,
} as const;

export const PRO014_WIZARD_TEXTS = {
  EDIT_MODE: (code: number): string => `Modo Edição - Campanha PRO014 #${code}`,
  LOADING: "Carregando dados da campanha...",
  LOAD_FAILED: COMMON_TEXTS.CAMPAIGN_LOAD_FAILED,
  ITEM_FALLBACK_NAME: (itemCode: string): string => `Item ${itemCode}`,
  ITEM_NAME: (name: string): string => `- ${name}`,
  ITEM_POSITION: (position: number): string => `#${position}`,
  BACK: COMMON_TEXTS.BACK,
  CONTINUE: "Continuar",
  SUCCESS_TITLE: COMMON_TEXTS.SUCCESS_TITLE,
  ERROR_TITLE: "Erro",
  CREATED: (description: string, code?: number): string =>
    `Campanha "${description}"${code ? ` #${code}` : ""} criada com sucesso!`,
  UPDATED: (description: string, code: number): string =>
    `Campanha "${description}" #${code} atualizada com sucesso!`,
  REMOVED_FROM_PREVIOUS: (count: number): string =>
    `${count} produto(s) removido(s) da campanha anterior. Nenhum outro campo foi alterado.`,
  CREATE_FAILED: "Erro ao criar campanha PRO014. Verifique os dados e tente novamente.",
  UPDATE_FAILED: "Erro ao atualizar campanha PRO014. Verifique os dados e tente novamente.",
  CONFLICT_TITLE: "Conflito de produtos",
  CONFLICT_DESCRIPTION: (count: number): string =>
    `${count} produto(s) já estão em outras campanhas ativas no mesmo período.`,
  UNRESOLVABLE_TITLE: "Não foi possível remover",
  UNRESOLVABLE_DESCRIPTION:
    "Alguns produtos não podem ser removidos da campanha anterior. Descarte-os para continuar.",
} as const;

export const PRO014_REVIEW_ITEMS_PREVIEW = 20;
export const PRO014_REVIEW_CONFLICTS_PREVIEW = 20;
export const DATE_LONG_FORMAT = "dd 'de' MMMM 'de' yyyy";

export const PRO014_DISCOUNT_TYPE_LABELS: Record<number, string> = {
  0: "Desconto integral na 2ª peça (mesmo item)",
  1: "Percentual na 2ª peça (mesmo item)",
  2: "Valor fixo de desconto (mesmo item)",
  3: "Desconto integral (itens da lista)",
  4: "Percentual (itens da lista)",
  5: "Valor fixo de desconto (itens da lista)",
  7: "Brinde - escolher item da lista",
  8: "Brinde - mesmo item",
};

export const PRO014_PAYMENT_METHOD_LABELS: Record<number, string> = {
  0: "Todas as formas",
  1: "Cartão da loja",
  2: "Dinheiro/Débito",
};

export const PRO014_REVIEW_TEXTS = {
  BASIC_INFO: "Informações Básicas",
  DESCRIPTION: "Descrição",
  PERIOD: "Período",
  START_DATE: "Data de Início",
  END_DATE: "Data de Término",
  STORES_TITLE: "Lojas",
  STORES_SCOPE: "Abrangência",
  ALL_STORES: COMMON_TEXTS.ALL_STORES,
  SPECIFIC_STORES: (count: number): string => `${count} loja(s) selecionada(s)`,
  RESTRICTION_TITLE: "Restrição por cor/tamanho",
  RESTRICTION_ON: (count: number): string =>
    `Ligada — ${count} produto(s), cada um com a sua grade`,
  RESTRICTION_OFF: "Desligada — a campanha vale para o produto inteiro",
  DISCOUNT_TITLE: "Configuração de Desconto",
  DISCOUNT_TYPE: "Tipo de Desconto",
  DISCOUNT_TYPE_FALLBACK: (type: number): string => `Tipo ${type}`,
  TAKE_PAY: "Leve / Pague",
  TAKE_PAY_VALUE: (take: number, pay: number): string => `Leve ${take} / Pague ${pay}`,
  DISCOUNT_VALUE: "Valor/Percentual",
  PAYMENT_METHOD: "Forma de Pagamento",
  PAYMENT_METHOD_FALLBACK: "Não especificado",
  BASE_VALUE: "Valor Base Mínimo",
  PRODUCTS: (count: number): string => `Produtos (${count})`,
  MORE_PRODUCTS: (count: number): string => `+${count} mais`,
  CONFLICTS_COUNT: (count: number): string => `${count} produto(s) em conflito`,
  EDIT_PRODUCTS: "Editar Produtos",
  MORE_CONFLICTS: (count: number): string => `+${count} produto(s) com conflito`,
  VALIDATING_BATCH: (current: number, total: number): string =>
    `Validando conflitos: lote ${current} de ${total}`,
  READY_TITLE: "Pronto para criar",
  CONFLICTS_TITLE: "Conflitos detectados",
  READY_DESCRIPTION: (action: string): string =>
    `Revise as informações acima e clique em "${action}" para finalizar.`,
  CONFLICTS_DESCRIPTION: (count: number): string =>
    `Existem ${count} produto(s) em conflito com outras campanhas. Remova-os ou ajuste as datas antes de submeter.`,
  ITEMS_SUMMARY: (count: number): string => `${count} produto(s) na campanha.`,
  STATUS_SUMMARY: (status: string, items: string): string => `${status} ${items}`,
  VALIDATION_TITLE: "Validação de Produtos",
  VALIDATION_DESCRIPTION:
    "Os códigos dos produtos serão validados no banco de dados ITE001 ao criar a campanha. Se algum código for inválido, a operação será cancelada.",
  VALIDATING: "Validando...",
  SUBMIT_LABEL: {
    [PRO014_WIZARD_MODE.CREATE]: "Criar Campanha",
    [PRO014_WIZARD_MODE.EDIT]: "Salvar Alterações",
  },
  SUBMITTING_LABEL: {
    [PRO014_WIZARD_MODE.CREATE]: "Criando...",
    [PRO014_WIZARD_MODE.EDIT]: "Salvando...",
  },
} as const;

export const PRO014_PRODUCTS_PAGE_SIZE = 50;
export const SKU_TEMPLATE_PATH = "/templates/sku-import-template.xlsx";
export const SKU_TEMPLATE_FILENAME = "sku-import-template.xlsx";
export const EXCEL_ACCEPT = ".xlsx,.xls";

export const PRO014_PRODUCTS_TEXTS = {
  TITLE: "Seleção de Produtos *",
  SUBTITLE: (min: number): string =>
    `Adicione os produtos que participarão da campanha (mínimo ${min})`,
  EXPIRED: "Campanha expirada — não é possível adicionar itens",
  CANCELLED: "Campanha cancelada — não é possível adicionar itens",
  SKU_LABEL: "Código do Produto (SKU)",
  SKU_PLACEHOLDER: "Ex: 101616",
  ADD: "Adicionar",
  ADD_HINT: 'Pressione Enter ou clique em "Adicionar" para incluir o produto',
  EXCEL_TITLE: "Importação em Massa via Excel",
  EXCEL_SUBTITLE: "Importe múltiplos SKUs de uma vez usando um arquivo Excel",
  DOWNLOAD_TEMPLATE: "Baixar Template",
  IMPORT_EXCEL: "Importar Excel",
  IMPORTING: (progress: number): string => `Importando... ${progress}%`,
  TIP_LABEL: "Dica:",
  TIP:
    "Baixe o template (já vem com 4 SKUs de exemplo prontos). Mantenha ou adicione mais SKUs na coluna A e importe. Somente a coluna SKU é lida, duplicatas são ignoradas automaticamente.",
  SELECTED_TITLE: "Produtos Selecionados",
  REMOVE_CONFLICTING: "Remover todos com conflito",
  PRODUCTS_COUNT: (count: number): string => `${count} ${count === 1 ? "produto" : "produtos"}`,
  SEARCH_PLACEHOLDER: "Buscar por SKU ou nome",
  FILTER_PLACEHOLDER: "Filtrar validade",
  FILTER_ARIA: "Filtrar produtos por conflito",
  FILTER_ALL: "Todos",
  FILTER_CLEAN: "Válidos",
  FILTER_CONFLICT: "Com conflito",
  EMPTY_TITLE: "Nenhum produto adicionado",
  EMPTY_HINT: (min: number): string => `Adicione pelo menos ${min} produto para continuar`,
  NOT_FOUND: "Nenhum produto encontrado",
  PREVIOUS: "Anterior",
  NEXT: "Próxima",
  VALIDATION_TITLE: "Validação de Produtos",
  VALIDATION_RULES: [
    "Os códigos importados via Excel são validados contra o cadastro (ITE001)",
    "Não é permitido adicionar produtos duplicados",
    "Todos os produtos serão ativados automaticamente",
  ],
  MIN_ITEMS_ERROR: (min: number): string => `Campanha deve ter ao menos ${min} produto`,
  MIN_ACTIVE_ITEMS_ERROR: (min: number): string =>
    `A campanha precisa manter ao menos ${min} produtos ativos. Desfaça uma remoção ou inclua outro produto.`,
  EMPTY_SKU_ERROR: "Digite um código de produto",
  DUPLICATE_SKU_ERROR: "Este produto já foi adicionado",
  REMOVE_FROM_PREVIOUS: "Remover da anterior",
  REMOVE_FROM_PREVIOUS_TITLE: "Marcar para remover este produto da campanha anterior ao salvar",
  NON_REMOVABLE_TITLE:
    "Campanhas por departamento/grupo (PRO013) não permitem remover um único produto.",
  DISCARD_ITEM_TITLE: "Descartar item desta campanha",
  DISCARD_ITEM_ARIA: (sku: string): string => `Descartar o produto ${sku}`,
  TEMPLATE_DOWNLOADED_TITLE: "Template baixado",
  TEMPLATE_DOWNLOADED: "Template Excel com 4 SKUs de exemplo baixado!",
  IMPORT_DONE_TITLE: "Importação concluída",
  IMPORT_DONE: (count: number): string => `${count} SKU(s) adicionado(s)`,
} as const;
